import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { register as registerAccount } from '@/lib/session';
import { useToast } from '@/components/base/Toast';
import { markUserLoginTime } from '@/components/feature/AuthGuard';

const STORAGE_KEY = 'register_form_draft';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const toast = useToast();

  const savedDraft = (() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const [formData, setFormData] = useState(() => savedDraft || {
    phone: '',
    password: '',
    confirmPassword: '',
    agree: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 保存表单草稿到 sessionStorage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // 注册成功或离开页面时可选清理；这里保持：只在成功注册后清理
  const clearDraft = () => sessionStorage.removeItem(STORAGE_KEY);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.phone.trim()) {
      newErrors.phone = t('common:errorPhoneEmpty');
    } else if (!/^1[3-9]\d{9}$/.test(formData.phone.trim())) {
      newErrors.phone = t('common:errorPhoneFormat');
    }
    if (!formData.password.trim()) {
      newErrors.password = t('common:errorPasswordEmpty');
    } else if (formData.password.length < 6) {
      newErrors.password = t('common:errorPasswordLength');
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('common:errorPasswordMismatch');
    }
    if (!formData.agree) {
      newErrors.agree = t('common:errorAgreeTerms');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setFormError('');

    const phone = formData.phone.trim();

    try {
      await registerAccount(phone, formData.password);
      markUserLoginTime();
      clearDraft();
      toast.showToast(t('common:registerSuccess'));
      navigate('/home', { replace: true });
    } catch (err) {
      const msg = String((err as Error)?.message || '');
      if (msg.includes('已注册') || msg.includes('已存在')) {
        setFormError(t('common:errorAccountExists'));
      } else {
        setFormError(t('common:errorRegisterFailed', { message: msg, code: '', status: '' }));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-brown-50 flex flex-col items-center relative">
      {/* Header */}
      <div className="w-full bg-brown-950 px-4 py-3 flex items-center sticky top-0 z-10">
        <button
          onClick={() => navigate('/login')}
          className="w-8 h-8 flex items-center justify-center text-gold-400 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-lg"></i>
        </button>
        <h1 className="text-gold-400 text-lg font-bold flex-1 text-center pr-8">{t('common:register')}</h1>
      </div>

      <div className="w-full max-w-[420px] px-4 pt-8 pb-12 md:pt-10 md:pb-16">
        <div className="bg-white rounded-2xl border border-brown-100 px-6 py-8 md:px-8 md:py-10">
          {/* Brand */}
          <div className="flex justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/7a4743b2-ba6c-451a-82cd-8cc6ed8c070f.png"
              alt="金安中国"
              className="h-24 w-auto object-contain max-w-full"
            />
          </div>

          <h2 className="text-2xl font-bold text-brown-900 mt-6 mb-2 text-center">{t('common:welcomeRegister')}</h2>
          <p className="text-brown-500 text-sm mb-8 text-center">{t('common:registerSubtitle')}</p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            {/* Phone */}
            <div>
              <div className="flex items-center border border-brown-200 rounded-lg px-4 py-3 bg-white transition-all duration-200 hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20">
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={11}
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, phone: val });
                    if (errors.phone) setErrors({ ...errors, phone: '' });
                  }}
                  placeholder={t('common:placeholderPhone')}
                  className="flex-1 text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
              </div>
              {errors.phone && <p className="text-xs text-red-500 mt-1 ml-1">{errors.phone}</p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center border border-brown-200 rounded-lg px-4 py-3 bg-white transition-all duration-200 hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (errors.password) setErrors({ ...errors, password: '' });
                  }}
                  placeholder={t('common:placeholderPassword')}
                  className="flex-1 text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="ml-2 w-5 h-5 flex items-center justify-center text-brown-400 cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`}></i>
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1 ml-1">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <div className="flex items-center border border-brown-200 rounded-lg px-4 py-3 bg-white transition-all duration-200 hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20">
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, confirmPassword: e.target.value });
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                  }}
                  placeholder={t('common:confirmPassword')}
                  className="w-full text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-xs text-red-500 mt-1 ml-1">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Agreement */}
            <div>
              <label className="flex items-start gap-2 cursor-pointer">
                <div
                  onClick={() => {
                    setFormData({ ...formData, agree: !formData.agree });
                    if (errors.agree) setErrors({ ...errors, agree: '' });
                  }}
                  className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 cursor-pointer ${
                    formData.agree
                      ? 'bg-gold-500 border-gold-500'
                      : 'border-brown-300'
                  }`}
                >
                  {formData.agree && (
                    <span className="w-3 h-3 flex items-center justify-center text-brown-950">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                  )}
                </div>
                <span className="text-brown-500 text-xs leading-relaxed">
                  {t('common:agreeTerms')}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/terms');
                    }}
                    className="text-gold-600 cursor-pointer whitespace-nowrap"
                  >
                    {t('common:userAgreement')}
                  </button>
                  {'和'}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/privacy');
                    }}
                    className="text-gold-600 cursor-pointer whitespace-nowrap"
                  >
                    {t('common:privacyPolicyDoc')}
                  </button>
                </span>
              </label>
              {errors.agree && <p className="text-xs text-red-500 mt-1 ml-6">{errors.agree}</p>}
            </div>

            {/* Form Error */}
            {formError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                  <i className="ri-error-warning-line text-sm"></i>
                </span>
                <span className="text-xs text-red-600">{formError}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 rounded-lg bg-gold-500 hover:bg-gold-600 text-brown-950 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap mt-2"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-brown-950/30 border-t-brown-950 rounded-full animate-spin"></span>
                  {t('common:loading')}
                </span>
              ) : (
                t('common:register')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-brown-500 text-sm">{t('common:hasAccount')}</span>
            <button
              onClick={() => navigate('/login')}
              className="ml-1.5 text-sm text-gold-600 font-medium cursor-pointer whitespace-nowrap"
            >
              {t('common:loginNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}